import { get, set } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Img } from 'react-image';
import { useMutation, useQueryClient } from 'react-query';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { guestUserSendOtp, guestUserVerifyOtp } from 'src/apis/hiringAssessment';
import AssessmentCompleted from 'src/components/GuestUserAssessment/AssessmentCompleted';
import GuestUserEmail from 'src/components/GuestUserAssessment/GuestUserEmail';
import GuestUserOTP from 'src/components/GuestUserAssessment/GuestUserOTP';
import WelcomeAssessment from 'src/components/GuestUserAssessment/WelcomeAssessment';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';
import BrowserWarningModal from 'src/components/scheduleMeetingDrawer/BrowserWarningModal';
import { useThemeContext } from 'src/context/ThemeProvider';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import classNames from 'classnames';
import {
	useGetHiringAssessment,
	useGetHiringAssessmentMeta,
	useGuestUserValidateJwt,
} from 'src/hooks/hiringAssessment.hooks';
import { useGetUserPreferenceMeetingsQuery } from 'src/hooks/meeting.hooks';
import { sendTrackingEvent } from 'src/utils/gtm';
import { RegisterUserInput, UserExperienceLevelType } from 'types';
import GuestUserAssessmentDashboard from 'src/components/GuestUserAssessment/GuestUserAssessmentDashboard';
import GuestUserNavbar from 'src/components/GuestUserAssessment/GuestUserNavbar';

const GuestUserAssessmentPage = () => {
	const { user } = useUserContext();
	const { themeMode } = useThemeContext();
	const [showSendOTPScreen, setShowSendOTPScreen] = useState(true);
	const [showVerifyOTPScreen, setShowVerifyOTPScreen] = useState(false);
	const [showAssessmentScreen, setShowAssessmentScreen] = useState(false);
	const [showCompletedScreen, setShowCompletedScreen] = useState(false);
	const [guestUserName, setGuestUserName] = useState<string>('');
	const [guestUserExperience, setGuestUserExperience] = useState<UserExperienceLevelType | undefined>(undefined);
	const [guestUserEmail, setGuestUserEmail] = useState<string>('');
	const [guestUserOTP, setGuestUserOTP] = useState(['', '', '', '', '', '']);
	const [token, setToken] = useState('');
	const { hiringAssessmentId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const shouldFetchData = location.pathname !== '/placed-hiring-assessment/all-assessments';
	const {
		data: hiringAssessmentMetaData,
		isLoading: hiringAssessmentMetaLoading,
		error: hiringAssessmentMetaError,
	} = useGetHiringAssessmentMeta({
		hiringAssessmentId: shouldFetchData ? hiringAssessmentId || '' : '',
	});

	const {
		data: preferenceMeetingData,
		isLoading: preferenceMeetingDataLoading,
		error: preferenceMeetingDataError,
	} = useGetUserPreferenceMeetingsQuery({
		hiringAssessmentId: shouldFetchData ? hiringAssessmentId : '',
		token: localStorage.getItem('guestUserJwtToken') || '',
	});

	const guestUserJwtToken = localStorage.getItem('guestUserJwtToken') || '';

	const { data: guestUserValidateJwtData, error: guestUserValidateJwtError } =
		useGuestUserValidateJwt(guestUserJwtToken);
	useEffect(() => {
		if (guestUserJwtToken) {
			setToken(guestUserJwtToken);
		}
	}, [guestUserJwtToken]);
	useEffect(() => {
		if (
			preferenceMeetingData &&
			preferenceMeetingData.completedMeetings &&
			preferenceMeetingData.completedMeetings.length >= 2
		) {
			setShowAssessmentScreen(false);
			setShowVerifyOTPScreen(false);
			setShowSendOTPScreen(false);
			setShowCompletedScreen(true);
		}
	}, [preferenceMeetingData]);

	useEffect(() => {
		if (guestUserValidateJwtData) {
			setShowVerifyOTPScreen(false);
			setShowSendOTPScreen(false);
		}
		if (
			guestUserValidateJwtData &&
			preferenceMeetingData &&
			preferenceMeetingData.completedMeetings &&
			preferenceMeetingData.completedMeetings.length < 2
		) {
			setShowAssessmentScreen(true);
		}
	}, [guestUserValidateJwtData, preferenceMeetingData]);

	const toast = useToast();
	const guestUserSendOtpMutation = useMutation(['guestUserSendOtp'], guestUserSendOtp, {
		onSuccess() {
			toast({
				title: 'OTP sent successfully!',
				type: 'success',
			});
			setShowSendOTPScreen(false);
			setShowVerifyOTPScreen(true);
		},

		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error while sending OTP: ${errorMessage}`,
				type: 'error',
			});
		},
	});
	const guestUserVerifyOtpMutation = useMutation(['guestUserVerifyOtp'], guestUserVerifyOtp, {
		onSuccess(data) {
			const jwtToken = data?.token;
			if (jwtToken) {
				toast({
					title: 'OTP verified successfully!',
					type: 'success',
				});
				localStorage.setItem('guestUserJwtToken', jwtToken);
				setShowAssessmentScreen(true);
				setShowVerifyOTPScreen(false);
			} else {
				toast({
					title: 'Error while verifying OTP',
					type: 'error',
				});
			}
		},

		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured ${errorMessage}`,
				type: 'error',
			});
		},
	});
	const handleSendOtp = () => {
		// send otp to the email
		guestUserSendOtpMutation.mutate({
			email: guestUserEmail,
			name: guestUserName,
			experienceLevel: guestUserExperience as UserExperienceLevelType,
		});
	};

	const handleVerifyOtp = () => {
		// send otp to the email
		guestUserVerifyOtpMutation.mutate({
			email: guestUserEmail,
			otp: guestUserOTP.join(''),
			name: guestUserName,
			experienceLevel: guestUserExperience as UserExperienceLevelType,
		});
	};

	const handleGuestUserComponents = () => {
		if (preferenceMeetingDataLoading) {
			return (
				// <div>Loading...</div>
				<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto " />
			);
		}
		if (showSendOTPScreen) {
			return (
				<GuestUserEmail
					isLoading={guestUserSendOtpMutation.isLoading}
					showSendOTPScreen={showSendOTPScreen}
					setShowSendOTPScreen={setShowSendOTPScreen}
					handleSendOtp={handleSendOtp}
					guestUserEmail={guestUserEmail}
					setGuestUserEmail={setGuestUserEmail}
					guestUserName={guestUserName}
					guestUserExperience={guestUserExperience}
					setGuestUserName={setGuestUserName}
					setGuestUserExperience={setGuestUserExperience}
					companyName={hiringAssessmentMetaData?.hiringAssessment?.company?.name}
					createdByName={hiringAssessmentMetaData?.hiringAssessment?.createdBy?.name}
					careerpath={hiringAssessmentMetaData?.hiringAssessment?.userPreferenceSnapshot.careerPath}
					hiringAssessmentDeadline={hiringAssessmentMetaData?.hiringAssessment?.hiringAssessmentDeadline || null}
				/>
			);
		} else if (showVerifyOTPScreen) {
			return (
				<GuestUserOTP
					isLoading={guestUserVerifyOtpMutation.isLoading}
					handleBackToEmail={() => {
						setShowSendOTPScreen(true);
						setShowVerifyOTPScreen(false);
					}}
					handleVerifyOtp={handleVerifyOtp}
					setGuestUserOTP={setGuestUserOTP}
					guestUserOTP={guestUserOTP}
					guestUserEmail={guestUserEmail}
					handleSendOtp={handleSendOtp}
					companyName={hiringAssessmentMetaData?.hiringAssessment?.company?.name}
					careerpath={hiringAssessmentMetaData?.hiringAssessment?.userPreferenceSnapshot.careerPath}
				/>
			);
		} else if (showAssessmentScreen && hiringAssessmentId) {
			return <WelcomeAssessment hiringAssessmentId={hiringAssessmentId} />;
		} else {
			return <AssessmentCompleted companyName={hiringAssessmentMetaData?.hiringAssessment?.company?.name} />;
		}
	};

	return (
		<>
			{user ? null : <GuestUserNavbar token={token} ForBackToHiringAssessmentId={hiringAssessmentId} />}
			<div className="flex justify-center flex-1">{handleGuestUserComponents()}</div>
		</>
	);
};

export default GuestUserAssessmentPage;
